import { Select } from '@mm/components';
import usersCRUDActions from '@mm/state/actions/UsersCRUDActions';
import { UnselectedFbPages } from '@mm/state/model/CompanyUsersModel';
import { selectBasicPages } from '@mm/state/selectors/facebookPageSelector';
import { selectActiveCompanyId } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { Switch } from 'antd';
import classNames from 'classnames';
import { isEqual } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import './NotificationSettings.pcss';

type Props = {
  notificationType:
    | 'allMessages'
    | 'needsFollowUp'
    | 'widgetTriggered'
    | 'assignedToMe'
    | 'unicornLead';
  // Note: Push means Mobile, but left this way for legacy users support
  deviceType:
    | 'pushNotifications'
    | 'desktopNotifications'
    | 'emailNotifications';
  notificationChangeCallback?: () => void;
  notificationPageChangeCallback?: () => void;
};

export const NotificationSettings = ({
  notificationType,
  deviceType,
  notificationChangeCallback,
  notificationPageChangeCallback,
}: Props) => {
  const dispatch: Dispatch<StoreState> = useDispatch();

  const companyId = useSelector(selectActiveCompanyId);

  const { id: userId, userAttributes } = useSelector(
    (state: StoreState) => state.session.session,
  );

  const notificationUnselectedPagesName =
    `${notificationType}UnselectedPages` as
      | 'allMessagesUnselectedPages'
      | 'needsFollowUpUnselectedPages'
      | 'widgetTriggeredUnselectedPages'
      | 'assignedToMeUnselectedPages'
      | 'unicornLeadUnselectedPages';

  const notificationSettings = userAttributes[deviceType];
  const notificationUnselectedPages: UnselectedFbPages =
    (notificationSettings &&
      notificationSettings[notificationUnselectedPagesName]) ||
    {};

  const basicPages = useSelector(selectBasicPages);

  const savedUnselectedPages = notificationUnselectedPages[companyId] || [];

  const mapPages = ({ id, name: label }) => ({
    key: id.toString(),
    id,
    label,
  });

  const defaultSelected = basicPages
    .filter(page => !savedUnselectedPages.includes(page.id))
    .map(mapPages);

  const [selectedPages, setSelectedPages] = useState(defaultSelected);

  const allPages = basicPages.map(mapPages);

  const [notificationDisabled, setNotificationDisabled] = useState(
    savedUnselectedPages.includes(-1),
  );

  const anyPageSelected = selectedPages.length;

  useEffect(() => {
    const unselectedPages = basicPages
      .filter(page => !selectedPages.find(p => p.id === page.id))
      .map(mapPages);

    const unselectedPagesForCompany = [
      ...unselectedPages.map(p => Number(p.key)),
      ...(notificationDisabled ? [-1] : []),
    ];

    const updatedUnselectedPages = {
      ...notificationUnselectedPages,
      [companyId]: unselectedPagesForCompany,
    };

    const updatedNotificationSettings = {
      [deviceType]: {
        ...notificationSettings,
        [notificationType]:
          notificationSettings[notificationType] || anyPageSelected,
        [notificationUnselectedPagesName]: updatedUnselectedPages,
      },
    };

    const saveChanges = async () => {
      await dispatch(
        usersCRUDActions.updateUserAttributes(
          companyId,
          userId,
          updatedNotificationSettings,
          false,
        ),
      );
    };

    if (
      !isEqual(
        { [deviceType]: notificationSettings },
        updatedNotificationSettings,
      )
    ) {
      saveChanges();
    }
  }, [
    basicPages.length,
    selectedPages,
    notificationDisabled,
    notificationUnselectedPages,
    notificationSettings,
  ]);

  return (
    <div className="notification-settings__pane-content--setttings-details__column">
      <div className="notification-settings__pane-content--setttings-details__column-subtitle">
        <div className="notification-settings__pane-content--setttings__switch">
          <Switch
            className="notification-settings__switch"
            checked={!notificationDisabled}
            onChange={() => {
              setNotificationDisabled(d => !d);
              if (notificationChangeCallback) notificationChangeCallback();
            }}
            size="small"
          />
        </div>
      </div>
      <div
        className={classNames(
          'notification-settings__pane-content--setttings-details__column-items',
          {
            'notification-settings__pane-content--setttings-details__column-items-disabled':
              notificationDisabled,
          },
        )}
      >
        <Select
          className="notification-settings__select"
          options={allPages}
          selected={selectedPages}
          disabled={notificationDisabled}
          hideCount
          hideClearButton
          withSelectAll
          selectAllLabel="All Accounts"
          placeholder="Select Pages"
          onChange={(items: typeof selectedPages) => {
            if (!isEqual(items, selectedPages)) {
              setSelectedPages(items);
            }
          }}
        />
      </div>
    </div>
  );
};
